import throttle from 'lodash/throttle';
import { useEffect, useMemo, useReducer } from 'react';
import { Dimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { DESKTOP_MIN_WIDTH, NON_DESKTOP_MAX_WIDTH } from 'src/constants/dimensions';
import { isAndroid, isWeb, isWebView } from 'src/utils/platform';
const deviceWidth = Dimensions.get('window').width;
const deviceHeight = isAndroid
    ? require('react-native-extra-dimensions-android').get('REAL_WINDOW_HEIGHT')
    : Dimensions.get('window').height;
const breakpoints = {
    sm: 600,
    md: 960,
    lg: 1200,
};
const getDimensionBreakpoint = (width) => {
    const result = {
        sm: false,
        md: false,
        lg: false,
    };
    if (width < breakpoints.sm) {
        result.sm = true;
    }
    else if (width < breakpoints.md) {
        result.md = true;
    }
    else {
        result.lg = true;
    }
    return result;
};
export default function useDimensions() {
    const safeAreaInsets = useSafeAreaInsets();
    const [count, rerender] = useReducer((x = 0) => x + 1, 0);
    useEffect(() => {
        if (isWebView || !isWeb)
            return;
        const throttledRerender = throttle(rerender, 200, {
            trailing: true,
            leading: false,
        });
        window.addEventListener('resize', throttledRerender);
        window.addEventListener('orientationchange', throttledRerender);
        return () => {
            window.removeEventListener('resize', throttledRerender);
            window.removeEventListener('orientationchange', throttledRerender);
        };
    }, []);
    const dimensions = useMemo(() => {
        if (isWebView) {
            const wvEnv = JSON.parse(window.ReactNativeWebView?.injectedObjectJson() ?? '{}');
            if (wvEnv.dimensions) {
                return wvEnv.dimensions;
            }
        }
        // Computing the device Width inside the useMemo, as
        // it was returing 0 due to unknown reasons, when
        // computed outside
        const dw = isWeb ? window.innerWidth : deviceWidth;
        const dh = isWeb ? window.innerHeight : deviceHeight;
        // Desktop web lifts the legacy phone-column cap and uses the full window.
        // Below the desktop breakpoint (and on native, which mirrors the old
        // behavior) the viewport stays capped, matching the web body max-width.
        const isDesktopWeb = isWeb && dw >= DESKTOP_MIN_WIDTH;
        return {
            device: {
                height: dh,
                width: dw,
            },
            viewPort: {
                height: dh,
                width: isDesktopWeb ? dw : Math.min(dw, NON_DESKTOP_MAX_WIDTH),
            },
            safeAreaInsets,
            breakpoint: {
                width: getDimensionBreakpoint(dw),
                height: getDimensionBreakpoint(dh),
            },
        };
    }, [safeAreaInsets, count]);
    return [dimensions];
}
